import React, { useState, useEffect } from "react";
import StatIncrement from "../modals/StatIncrement";
import './CreateCharacter.css';
import PortraitSelector from "./PortraitSelector";
import ThemeSwitcher from '../ThemeSwitcher';

const INITIAL_VALUES = { strength: 0, agility: 0, toughness: 0, intelligence: 0, wisdom: 0, charisma: 0 };

function CreateCharacter({ createInfo, sendMessage, toggleCreate, currentTheme, onThemeChange }) {
    const [showPortraitSelector, setShowPortraitSelector] = useState(false);
    const [selectedImage, setSelectedImage] = useState(-1);
    const [characterName, setCharacterName] = useState('');
    const [characterGender, setCharacterGender] = useState(0);
    const [characterClass, setCharacterClass] = useState(null);
    const [characterRace, setCharacterRace] = useState(null);

    const [trainStats, setTrainStats] = useState(INITIAL_VALUES);
    const [review, setReview] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    useEffect(() => {
        if (createInfo && createInfo.races && createInfo.races.length > 0) {
            setCharacterRace(createInfo.races[0]);
        }
        if (createInfo && createInfo.classList && createInfo.classList.length > 0) {
            setCharacterClass(createInfo.classList[0]);
        }
        if (createInfo) {
            setCharacterGender(0);
            setTrainStats(INITIAL_VALUES);
        }
    }, [createInfo]);

    const increaseStat = (name) => {
        const totalTrainStats = Object.values(trainStats).reduce((a, b) => a + b, 0);
        if (totalTrainStats < createInfo.statPoints) {
            setTrainStats(prevStats => ({ 
                ...prevStats, 
                [name.toLowerCase()]: prevStats[name.toLowerCase()] + 1 
            }));
            setErrorMessages([]);
        }
    }

    const decreaseStat = (name) => {
        if (trainStats[name.toLowerCase()] > 0) {
            setTrainStats(prevStats => ({ 
                ...prevStats, 
                [name.toLowerCase()]: prevStats[name.toLowerCase()] - 1 
            }));
            setErrorMessages([]);
        }
    }

    function updateRaceInfo(value) {
        const selectedRace = createInfo.races.find((race) => race.name === value);
        if (selectedRace) {
            setCharacterRace(selectedRace);
        } else {
            setCharacterRace(createInfo.races[0]);
        }
        setTrainStats(INITIAL_VALUES);
    }

    function updateSelectedClass(value) {
        const selectedClass = createInfo.classList.find((c) => c.name === value);
        if (selectedClass) {
            setCharacterClass(selectedClass);
        } else {
            setCharacterClass(createInfo.classList[0]);
        }
    }

    function updateGender(value) {
        value === "male" ? setCharacterGender(0) : setCharacterGender(1);
    }

    const createCharacter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.type === 'submit') {
            console.log(selectedImage, characterName, characterGender, characterClass, characterRace, trainStats);
            const errors = [];
            if (selectedImage < 0) {
                errors.push('Select a portrait');
            }
            if (characterName.length === 0 || characterName.length > 20) {
                errors.push('Invalid character name');
            }
            if (characterClass === null) {
                errors.push('Choose a class')
            }
            if (characterRace === null) {
                errors.push('Choose a race')
            }
            if (Object.values(trainStats).reduce((a, b) => a + b, 0) < createInfo.statPoints) {
                errors.push('You have stat points to use')
            }
            if (errors.length === 0) {
                setErrorMessages([]);
                setReview(true);
            } else {
                setErrorMessages(errors);
            }
        }
    }

    function createCharacterRequest() {
        sendMessage('003' + characterName + "/" + characterGender + "/" + characterClass.name + "/" + characterRace.name + "/"
            + trainStats.strength + "/" + trainStats.agility + "/" + trainStats.toughness + "/" + trainStats.intelligence + "/" + trainStats.wisdom + "/" + trainStats.charisma + "/" + selectedImage
        );
        toggleCreate();
    }

    function ReviewCharacter() {
        return (
            <div className="review-container">
                <div className="review-header">
                    <h1>Review Your Character</h1>
                    <div className="review-portrait">
                        <img 
                            src={require('../../Assets/gfx/' + ((selectedImage > 0) ? selectedImage : '588') + '.webp')} 
                            alt="Character Portrait"
                        />
                    </div>
                </div>
                
                <div className="review-content">
                    <div className="review-section">
                        <h3>
                            <span className="material-symbols-outlined">person</span>
                            Basic Information
                        </h3>
                        <div className="review-details">
                            <p><strong>Name:</strong> {characterName}</p>
                            <p><strong>Race:</strong> {characterRace.name}</p>
                            <p><strong>Class:</strong> {characterClass.name}</p>
                            <p><strong>Gender:</strong> {characterGender === 0 ? 'Male' : 'Female'}</p>
                            <div className="stat-summary">
                                <strong>Stat Allocation:</strong>
                                <div className="allocated-stats">
                                    {trainStats.strength > 0 && <span className="stat-pill">Strength +{trainStats.strength}</span>}
                                    {trainStats.agility > 0 && <span className="stat-pill">Agility +{trainStats.agility}</span>}
                                    {trainStats.toughness > 0 && <span className="stat-pill">Toughness +{trainStats.toughness}</span>}
                                    {trainStats.wisdom > 0 && <span className="stat-pill">Wisdom +{trainStats.wisdom}</span>}
                                    {trainStats.intelligence > 0 && <span className="stat-pill">Intelligence +{trainStats.intelligence}</span>}
                                    {trainStats.charisma > 0 && <span className="stat-pill">Charisma +{trainStats.charisma}</span>}
                                    {Object.values(trainStats).every(val => val === 0) && <span className="no-stats">No stat points allocated</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="create-actions">
                    <button type="button" className="back" onClick={() => setReview(false)}>
                        <span className="material-symbols-outlined">edit</span>
                        Edit Character
                    </button>
                    <button type="button" className="sign" onClick={createCharacterRequest}>
                        <span className="material-symbols-outlined">check_circle</span>
                        Confirm Creation
                    </button>
                </div>
            </div>
        );
    }

    function DisplayErrors() {
        return (
            <div className="error-content">
                <h1>Error</h1>
                <div>
                    {errorMessages.map((e, i) => <p key={i}>{e}</p>)}
                </div>
                <div className="create-actions">
                    <button className="back" onClick={() => setErrorMessages([])}>
                        <span className="material-symbols-outlined">arrow_back</span>
                        Back
                    </button>
                </div>
            </div>
        );
    }

    if (!createInfo || !characterRace || !characterClass) {
        return (
            <div className="create-character-container">
                <div className="loading-state">
                    <h1>Loading Character Creation</h1>
                    <p>Please wait while we prepare the character creation form...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <form 
                className="create-character-container" 
                onSubmit={createCharacter}
                onClick={(e) => {
                    if (e.target.closest('.statbox') || 
                        e.target.closest('.stat-button') || 
                        e.target.closest('.stat-values')) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                {review && <ReviewCharacter />}
                {errorMessages.length > 0 && <DisplayErrors />}
                {showPortraitSelector && (
                    <PortraitSelector 
                        selectedImage={selectedImage} 
                        setSelectedImage={setSelectedImage} 
                        setShowPortraitSelector={setShowPortraitSelector}
                        currentTheme={currentTheme}
                    />
                )}
                {!review && !errorMessages.length && (
                    <>
                        <div className="create-container">
                            <div className="create-form">
                                <div>
                                    <label htmlFor="portrait">
                                        <span className="material-symbols-outlined">account_circle</span>
                                        Character Portrait
                                    </label>
                                    <div className="cportrait">
                                        <img 
                                            src={require('../../Assets/gfx/' + ((selectedImage > 0) ? selectedImage : '588') + '.webp')} 
                                            alt="Character Portrait"
                                        />
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowPortraitSelector(true);
                                            }}
                                            aria-label="Select character portrait"
                                        >
                                            <span className="material-symbols-outlined">image</span>
                                            Select Portrait
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="name">
                                        <span className="material-symbols-outlined">badge</span>
                                        Character Name
                                    </label>
                                    <input 
                                        id="name"
                                        type="text"
                                        placeholder="Enter character name"
                                        onChange={(e) => setCharacterName(e.currentTarget.value)}
                                        value={characterName}
                                        maxLength={20}
                                        autoFocus 
                                        aria-label="Character name"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="gender">
                                        <span className="material-symbols-outlined">wc</span>
                                        Gender
                                    </label>
                                    <select 
                                        id="gender"
                                        onChange={e => updateGender(e.currentTarget.value)}
                                        value={characterGender}
                                        aria-label="Character gender"
                                    >
                                        <option value={0}>Male</option>
                                        <option value={1}>Female</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="race">
                                        <span className="material-symbols-outlined">groups</span>
                                        Race
                                    </label>
                                    <select 
                                        id="race"
                                        onChange={e => updateRaceInfo(e.currentTarget.value)}
                                        value={characterRace.name}
                                        aria-label="Character race"
                                    >
                                        {createInfo.races.map(race => (
                                            <option key={race.name} value={race.name}>{race.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="clazz">
                                        <span className="material-symbols-outlined">auto_awesome</span>
                                        Class
                                    </label>
                                    <select 
                                        id="clazz"
                                        onChange={e => updateSelectedClass(e.currentTarget.value)}
                                        value={characterClass.name}
                                        aria-label="Character class"
                                    >
                                        {createInfo.classList.map((c) => (
                                            <option key={c.name} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                            </div>

                            <div className="create-info">
                                <div className="stat-section">
                                    <div className="stat-points-container">
                                        <div className="stat-points-header">
                                            <span className="stat-points-title">Available Stat Points</span>
                                            <span className="stat-points-value">
                                                {createInfo.statPoints - Object.values(trainStats).reduce((a, b) => a + b, 0)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="stat-grid">
                                        <StatIncrement 
                                            name="Strength" 
                                            value={characterRace?.str || 0} 
                                            increase={trainStats.strength} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Increases melee damage and parry effectiveness"
                                        />
                                        <StatIncrement 
                                            name="Agility" 
                                            value={characterRace?.agi || 0} 
                                            increase={trainStats.agility} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Reduces stamina recovery time and increases cast speed"
                                        />
                                        <StatIncrement 
                                            name="Toughness" 
                                            value={characterRace?.tog || 0} 
                                            increase={trainStats.toughness} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Increases health and health regeneration"
                                        />
                                        <StatIncrement 
                                            name="Wisdom" 
                                            value={characterRace?.wis || 0} 
                                            increase={trainStats.wisdom} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Increases mana and mana regeneration"
                                        />
                                        <StatIncrement 
                                            name="Intelligence" 
                                            value={characterRace?.intel || 0} 
                                            increase={trainStats.intelligence} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Increases spell damage and critical hit chance"
                                        />
                                        <StatIncrement 
                                            name="Charisma" 
                                            value={characterRace?.chr || 0} 
                                            increase={trainStats.charisma} 
                                            increaseStat={increaseStat} 
                                            decreaseStat={decreaseStat}
                                            tooltip="Increases buff and healing effectiveness"
                                        />
                                    </div>
                                </div>

                                <div className="info-section">
                                    <h3>
                                        <span className="material-symbols-outlined">groups</span>
                                        Race Attributes
                                    </h3>
                                    <div className="info-text">
                                        {characterRace?.raceAttributes?.map((attr, index) => (
                                            <p key={index}>{attr}</p>
                                        )) || <p>No race attributes available.</p>}
                                        {characterRace?.racialTraits && (
                                            <>
                                                <p><strong>Racial Traits:</strong></p>
                                                {characterRace.racialTraits.map((trait, index) => (
                                                    <p key={index}>{trait}</p>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="class-information-section">
                            <div className="info-section">
                                <h3>
                                    <span className="material-symbols-outlined">auto_awesome</span>
                                    Class Information
                                </h3>
                                <div className="info-text">
                                    <p>{characterClass?.description || 'No class information available.'}</p>
                                    {characterClass?.abilities && (
                                        <>
                                            <p><strong>Class Abilities:</strong></p>
                                            {characterClass.abilities.map((ability, index) => (
                                                <p key={index}>{ability}</p>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="create-actions">
                            <button type="button" className="back" onClick={toggleCreate}>
                                <span className="material-symbols-outlined">arrow_back</span>
                                Back to Characters
                            </button>
                            <button type="submit" className="sign">
                                <span className="material-symbols-outlined">add_circle</span>
                                Create Character
                            </button>
                        </div>
                    </>
                )}
            </form>
            <ThemeSwitcher currentTheme={currentTheme} onThemeChange={onThemeChange} />
        </>
    );
}

export default CreateCharacter;